import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { TableCell, TableRow, Typography } from "@mui/material";
import { Add } from "@mui/icons-material";

import Table from "../../components/Table";
import Modal from "../../components/Modal";
import FilterInput from "../../components/Filter/FilterInput";
import FilterLabel from "../../components/Filter/FilterLabel";
import Button from "../../components/Button";
import EmptyState from "../../components/EmptyState";
import Pagination from "../../components/Pagination";
import SearchInput from "../../components/SearchInput";
import { ROUTE_PATHS } from "../../utils/RoutesPaths";
import {
  renderTextEllipsis,
  setPageMainTitle,
  textContainOnlySpaces,
} from "../../utils/Helpers";
import { DeleteBgIcon, EditBgIcon, ViewBgIcon } from "../../utils/Icons";
import {
  deleteCategoryRequest,
  getCategoriesRequest,
} from "../../store/Categories/actions";
import { getSpecialtiesRequest } from "../../store/Specialties/actions";
import messages from "../../assets/locale/messages";
import "./Service-provider-categories.scss";

const ServiceProviderCategories = () => {
  const history = useHistory();
  const dispatch = useDispatch();

  const lang = useSelector((state) => state.locale.lang);
  const isLoading = useSelector((state) => state.loader);
  const meta = useSelector((state) => state.categories.meta);
  const list = useSelector((state) => state.categories.list);
  const specialtiesOptions = useSelector((state) => state.specialties.list);

  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [filterSpecialtyValue, setFilterSpecialtyValue] = useState([]);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleteServiceProviderCategoryId, setdeleteServiceProviderCategoryId] =
    useState(null);

  const { shared, serviceProviderCategories, pagesTitles } = messages[lang];

  const getCategories = (pageNo, search, arrayOfSpecialties) => {
    dispatch(
      getCategoriesRequest({
        page: pageNo,
        items: 10,
        title: search,
        specialty_ids: arrayOfSpecialties.join(','),
      })
    );
  };

  const getSpecialtiesLookup = () => {
    dispatch(
      getSpecialtiesRequest({
        page: -1,
      })
    );
  };

  useEffect(() => {
    setPageMainTitle("categoriesManagement");
  }, []);

  useEffect(() => {
    getCategories(page, searchValue, filterSpecialtyValue);
    getSpecialtiesLookup();
  }, [page]);

  useEffect(() => {
    if (
      (searchValue.length >= 3 || searchValue.length === 0) &&
      !textContainOnlySpaces(searchValue)
    ) {
      setPage(1);
      getCategories(1, searchValue, filterSpecialtyValue);
    }
  }, [searchValue]);

  useEffect(() => {
    setPage(1);
    getCategories(1, searchValue, filterSpecialtyValue);
  }, [filterSpecialtyValue]);

  const handleEditServiceProviderCategory = (id) => {
    history.push(ROUTE_PATHS.editServiceProviderCategories.replace(":id", id));
  };
  const handleViewServiceProviderCategory = (id) => {
    history.push(ROUTE_PATHS.viewServiceProviderCategories.replace(":id", id));
  };
  const handleDeleteClick = (id) => {
    setdeleteServiceProviderCategoryId(id);
    setOpenDeleteConfirm(true);
  };
  const handleDeleteServiceProviderCategory = () => {
    dispatch(
      deleteCategoryRequest({
        id: deleteServiceProviderCategoryId,
        currentPage: meta.currentPage,
      })
    );
    setOpenDeleteConfirm(false);
  };
  const handleCloseDeleteConfirmModal = () => {
    setOpenDeleteConfirm(false);
  };

  const getFilterValues = () => {
    const specialties = filterSpecialtyValue
      .map((spec) => specialtiesOptions.find((elt) => elt.id === spec))
      .map((spec) => ({
        id: spec.id,
        label: lang === "en" ? spec.title_en : spec.title_ar,
      }));
    return specialties;
  };

  const renderHeaderSection = (showActions) => (
    <>
      <div className="categories-header d-flex justify-content-between mb-4 gap-2">
        <div className="title-search d-flex flex-column flex-md-row">
          <Typography
            variant="h4"
            sx={{ flexGrow: 1 }}
            className={`page-title ${lang === "en" ? "me-2" : "ms-2"}`}
          >
            {pagesTitles.serviceProviderCategories}
          </Typography>
          {showActions && (
            <SearchInput
              name="search"
              value={searchValue}
              placeholder={serviceProviderCategories.list.search}
              onChange={(value) => {
                setSearchValue(value);
              }}
            />
          )}
        </div>
        {showActions && (
          <div className="add-btn-wrapper">
            <Button
              label={
                <span className="d-flex align-items-center">
                  <Add fontSize="large" />
                  <span className="ps-1 ">
                    {serviceProviderCategories.list.CreateCategoriesButton}
                  </span>
                </span>
              }
              labelClass="fsize-16 text-white pe-2"
              onClick={() => {
                history.push(ROUTE_PATHS.addServiceProviderCategories);
              }}
            />
          </div>
        )}
      </div>

      {showActions && (
        <div className="filter-label-wrapper d-flex align-items-center mb-4 w-50">
          <FilterLabel />
          <FilterInput
            placeholder={
              serviceProviderCategories.list.filter.filtersPlaceHolders
                .filterBySpecialty
            }
            options={specialtiesOptions?.map((option) => ({
              id: option.id,
              label: lang === "en" ? option.title_en : option.title_ar,
            }))}
            value={getFilterValues()}
            name="specialty Filter"
            onChange={(value) => {
              setFilterSpecialtyValue(value.map((spec) => spec.id));
            }}
            inputClassName=""
            filterWrapperClass="my-3 my-md-0 w-100"
            isMultiple={true}
          />
        </div>
      )}
    </>
  );

  const ServiceProviderCategoriesList = () => {
    if (
      !isLoading &&
      list?.length === 0 &&
      !searchValue &&
      filterSpecialtyValue?.length === 0
    ) {
      return (
        <>
          {renderHeaderSection(false)}
          <div className="bg-white rounded-4 categoriess-page d-flex justify-content-center align-items-center flex-column">
            <EmptyState
              subTitle={shared.noData}
              description={serviceProviderCategories.list.noDataDescription}
              ButtonLabel={
                serviceProviderCategories.list.CreateCategoriesButton
              }
              handleBtnAction={() => {
                history.push(ROUTE_PATHS.addServiceProviderCategories);
              }}
            />
          </div>
        </>
      );
    } else if ( 
      !isLoading &&
      (searchValue || filterSpecialtyValue?.length > 0) &&
      list?.length === 0
    ) {
      return (
        <>
          {renderHeaderSection(true)}
          <div className="bg-white rounded-4 categoriess-page d-flex justify-content-center align-items-center flex-column">
            <EmptyState subTitle={shared.noSearch} showActionButton={false} />
          </div>
        </>
      );
    } else {
      return (
        <>
          <div>
            {renderHeaderSection(true)}
            <div className="bg-white rounded-4 my-3 categoriess-table-list">
              <Table
                className="rounded-4"
                headlines={[shared.lists.enTitle, shared.lists.arTitle]}
                hasActions={true}
                rows={list?.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell className="start font-regular text-dark-blue fsize-14">
                      {renderTextEllipsis(row.title_en || "-")}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14">
                      {renderTextEllipsis(row.title_ar || "-")}
                    </TableCell>
                    <TableCell className="font-regular text-dark-blue fsize-14">
                      <div className="table-icons-container gap-4">
                        <EditBgIcon
                          className="pointer"
                          onClick={() =>
                            handleEditServiceProviderCategory(row.id)
                          }
                        />
                        <ViewBgIcon
                          className="pointer"
                          onClick={() =>
                            handleViewServiceProviderCategory(row.id)
                          }
                        />
                        <DeleteBgIcon
                          className="pointer"
                          onClick={() => handleDeleteClick(row.id)}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              />
              <div className="d-flex justify-content-center py-3">
                {meta?.pages > 1 && (
                  <Pagination
                    count={meta?.pages}
                    page={meta?.currentPage || page}
                    handleChange={(event, page) => {
                      setPage(page);
                    }}
                    defaultPage={1}
                  />
                )}
              </div>
            </div>
          </div>
          <Modal
            open={openDeleteConfirm}
            handleClose={handleCloseDeleteConfirmModal}
            title={serviceProviderCategories.deleteCategory.title}
            contentClass="mb-5 pb-2 text-primary"
            content={
              <span>
                {serviceProviderCategories.deleteCategory.deleteConfirmationMsg}
              </span>
            }
            isContentText={false}
            confirmBtnTxt={shared.delete}
            maxWidth="xs"
            cancelBtnTxt={shared.cancel}
            handleConfirm={handleDeleteServiceProviderCategory}
            handleCancel={handleCloseDeleteConfirmModal}
            modalClass="delete-categories-modal"
            actionsClass="container px-4"
            confirmBtnClass="btn-secondary fsize-14 text-white py-2"
            cancelBtnClass="fsize-14 py-2"
          />
        </>
      );
    }
  };

  return (
    <div className="service-categories-container">
      {ServiceProviderCategoriesList()}
    </div>
  );
};

export default ServiceProviderCategories;
